import React, { useState } from 'react';
import { ArrowRight, Factory, Settings, Zap, Award, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { machines, components, services, technologies } from '../mockData';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const Home = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const categories = ['All', 'Cooker Plant', 'Steam Plant', 'Drier', 'Pre-Cleaner'];

  const filteredMachines = selectedCategory === 'All' 
    ? machines 
    : machines.filter(m => m.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-50 via-white to-brand-50 opacity-70"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <Badge className="bg-brand-100 text-brand-700 hover:bg-brand-200 border-0">
                <Zap size={14} className="mr-1" />
                Next-Gen Automation
              </Badge>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Revolutionizing
                <span className="block mt-2 bg-gradient-to-r from-brand-600 to-brand-600 bg-clip-text text-transparent leading-normal">
                  Paddy Processing
                </span>
              </h1>
              
              <p className="text-xl text-gray-600 leading-relaxed">
                Advanced automated solutions for rice mills with AI-powered controls, 
                precision engineering, and industry-leading efficiency since 2010.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-gradient-to-r from-brand-600 to-brand-600 hover:from-brand-700 hover:to-brand-700 text-white shadow-lg hover:shadow-xl transition-all duration-200">
                    Request Quote <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-brand-600 text-brand-700 hover:bg-brand-50"
                  onClick={() => {
                    document.getElementById('machines')?.scrollIntoView({ 
                      behavior: 'smooth',
                      block: 'start'
                    });
                  }}
                >
                  View Products
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 pt-8">
                <div>
                  <div className="text-3xl font-bold text-gray-900">14+</div>
                  <div className="text-sm text-gray-600">Years Experience</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900">500+</div>
                  <div className="text-sm text-gray-600">Plants Installed</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900">98%</div>
                  <div className="text-sm text-gray-600">Satisfaction</div>
                </div>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative">
              <div className="absolute -top-4 -right-4 w-72 h-72 bg-gradient-to-br from-brand-200 to-brand-200 rounded-full blur-3xl opacity-30"></div>
              <img
                src="https://sales.rallyappliances.in/media/files/test.0103.png"
                alt="Industrial Automation"
                className="relative rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md rounded-xl p-6 shadow-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Automation Rate</div>
                    <div className="text-2xl font-bold text-brand-600">99.5%</div>
                  </div>
                  <div className="w-16 h-16 bg-gradient-to-br from-brand-500 to-brand-600 rounded-full flex items-center justify-center">
                    <Factory className="text-white" size={28} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-brand-100 text-brand-700 mb-4">
              <Zap size={14} className="mr-1" />
              Powered by Innovation
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900">Next-Gen Technologies</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {technologies.map((tech, index) => (
              <Card key={index} className="border-2 hover:border-brand-500 hover:shadow-lg transition-all duration-200 cursor-pointer group">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl font-bold text-brand-600 mb-2 group-hover:scale-110 transition-transform">{tech.abbr}</div>
                  <div className="text-xs text-gray-600">{tech.name}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Machines Section */}
      <section id="machines" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-brand-100 text-brand-700 mb-4">
              <Settings size={14} className="mr-1" />
              Our Products
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Advanced Processing Machines</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Precision-engineered solutions for every stage of paddy processing
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2.5 rounded-lg font-medium transition-all duration-200 ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-brand-600 to-brand-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-brand-500'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Machines Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredMachines.map((machine) => (
              <Card key={machine.id} className="group hover:shadow-2xl transition-all duration-300 border-0 overflow-hidden">
                <div className="relative overflow-hidden">
                  <img
                    src={machine.image}
                    alt={machine.name}
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-white/90 text-brand-700 backdrop-blur-sm">
                      {machine.category}
                    </Badge>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-brand-600 transition-colors">
                    {machine.name}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">{machine.description}</p>
                  
                  <div className="flex flex-wrap gap-2">
                    {machine.features.map((feature, idx) => (
                      <span key={idx} className="text-xs px-3 py-1 bg-brand-50 text-brand-700 rounded-full">
                        {feature}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Components Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Essential Components</h2>
            <p className="text-xl text-gray-600">Complete your processing plant with our precision-built components</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {components.map((component) => (
              <Card key={component.id} className="group hover:shadow-xl transition-all duration-200 cursor-pointer border-0">
                <CardContent className="p-6">
                  <h4 className="font-semibold text-sm text-gray-900 text-center group-hover:text-brand-600 transition-colors">
                    {component.name}
                  </h4>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-brand-100 text-brand-700 mb-4">
              <Award size={14} className="mr-1" />
              What We Offer
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Comprehensive Services</h2>
            <p className="text-xl text-gray-600">End-to-end solutions for your paddy processing needs</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service) => (
              <Card key={service.id} className="border-0 bg-white hover:shadow-xl transition-all duration-200 group">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-brand-500 to-brand-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    {service.icon === 'Drafting Compass' && <Factory className="text-white" size={24} />}
                    {service.icon === 'Factory' && <Factory className="text-white" size={24} />}
                    {service.icon === 'Settings' && <Settings className="text-white" size={24} />}
                    {service.icon === 'Headphones' && <Award className="text-white" size={24} />}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-brand-900 via-slate-900 to-gray-900 text-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-white/20 text-white backdrop-blur-sm mb-4">
              <Play size={14} className="mr-1" />
              See Us In Action
            </Badge>
            <h2 className="text-4xl font-bold mb-4">Watch Our Plants in Operation</h2>
            <p className="text-xl text-gray-300">Experience the precision and efficiency of Vigyan automation</p>
          </div>

          <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
            <div className="aspect-video bg-gray-800">
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                title="Vigyan Plant Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white to-brand-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Ready to Modernize Your Processing Plant?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Get in touch with our experts for a customized solution tailored to your needs
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-gradient-to-r from-brand-600 to-brand-600 hover:from-brand-700 hover:to-brand-700 text-white text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all duration-200">
              Contact Us Today <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;